import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {
  FilteredListPageLayout,
  type FilteredListPageLayoutContentProps,
} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar-ui/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar-ui/constants'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import type {CSSProperties, ReactNode} from 'react'

import {useRepoIssuesSidebarPanes} from './use-repo-issues-sidebar-panes'
import {useRepoIssuesSidebarLayoutEnabled} from './use-sidebar-layout-enabled'

const SIDEBAR_ARIA_LABEL = 'Issues sidebar navigation'
const DEFAULT_SIDEBAR_DIALOG_TITLE = 'Issues'

export const REPO_ISSUES_TAB_CONTENT_STYLE = {
  paddingBlockStart: 'var(--base-size-8)',
} as const

export interface RepoIssuesPageLayoutProps {
  children: ReactNode
  /** Page title for the header. Pass `null` (or omit when no actions either) to suppress the header. */
  title?: ReactNode
  /** Trailing actions for the page header (typically a primary CTA). */
  actions?: ReactNode
  /** Content rendered above the title (typically a back-link or breadcrumbs). */
  contextArea?: ReactNode
  /** Whether to render a narrow-only header containing the sidebar trigger when the page owns its visible header. */
  showNarrowSidebarTrigger?: boolean
  /** Title for the narrow-viewport sidebar Dialog. */
  dialogTitle?: string
  /** Render the sidebar content. Receives `true` when the sidebar is collapsible. */
  renderSidebar: (collapsible?: boolean) => ReactNode
  /**
   * Server-read seed for the sidebar's initial expanded state (prevents a flash
   * on hard navigation). Pages pass it from the `issuesLayoutRoute` payload.
   */
  initialExpanded?: boolean
  /**
   * Maximum width of the header and content. The legacy `ThreePanesLayout` ignores it.
   * @default 'xlarge'
   */
  width?: FilteredListPageLayoutContentProps['width']
  /** Padding applied by the content region. The legacy `ThreePanesLayout` ignores it. */
  contentPadding?: FilteredListPageLayoutContentProps['padding']
  /** Inline style overrides for the content region. The legacy `ThreePanesLayout` ignores it. */
  contentStyle?: CSSProperties
}

/**
 * Layout wrapper for every repo-scoped issues page. When
 * `repo_issues_sidebar_layout` is on, renders the new
 * `<FilteredListPageLayout>` (a `SidebarPageLayout`-composed template that
 * additionally provides `FilterBar` / `Results` / `HeaderActions` slots —
 * page-level slots are adopted by `IssueIndexPage` and the migrated
 * Labels/Milestones/Projects pages). An optional `<PageHeader>` is rendered
 * if a `title` or `actions` prop is passed. When the flag is off, falls
 * back to the legacy `<ThreePanesLayout>` via `useRepoIssuesSidebarPanes()`.
 */
export function RepoIssuesPageLayout({
  children,
  title,
  actions,
  contextArea,
  showNarrowSidebarTrigger = false,
  dialogTitle = DEFAULT_SIDEBAR_DIALOG_TITLE,
  renderSidebar,
  initialExpanded,
  width,
  contentPadding,
  contentStyle,
}: RepoIssuesPageLayoutProps) {
  const useSidebarPageLayout = useRepoIssuesSidebarLayoutEnabled()
  const showHeader = useSidebarPageLayout && (title != null || actions != null || contextArea != null)

  const panes = useRepoIssuesSidebarPanes(renderSidebar, initialExpanded)

  if (useSidebarPageLayout) {
    return (
      <FilteredListPageLayout
        storageKey={SIDEBAR_STORAGE_KEY}
        initialExpanded={initialExpanded}
        showFooterBorder
        fitViewport
      >
        {showHeader ? (
          <FilteredListPageLayout.Header width={width}>
            <FilteredListPageLayout.PageHeader>
              {contextArea != null && (
                <FilteredListPageLayout.PageHeader.ContextArea hidden={false}>
                  {contextArea}
                </FilteredListPageLayout.PageHeader.ContextArea>
              )}
              {title != null && (
                <FilteredListPageLayout.PageHeader.TitleArea>
                  <FilteredListPageLayout.PageHeader.Title as="h1">{title}</FilteredListPageLayout.PageHeader.Title>
                </FilteredListPageLayout.PageHeader.TitleArea>
              )}
              {actions != null && (
                <FilteredListPageLayout.PageHeader.Actions>{actions}</FilteredListPageLayout.PageHeader.Actions>
              )}
            </FilteredListPageLayout.PageHeader>
          </FilteredListPageLayout.Header>
        ) : showNarrowSidebarTrigger ? (
          <FilteredListPageLayout.Header width={width} hidden={{narrow: false, regular: true, wide: true}}>
            <FilteredListPageLayout.PageHeader />
          </FilteredListPageLayout.Header>
        ) : null}
        <FilteredListPageLayout.Sidebar
          aria-label={SIDEBAR_ARIA_LABEL}
          dialogTitle={dialogTitle}
          scrollable
          footer={() => (
            <>
              <CollapsibleFeedbackLink feedbackEnabled />
              <CollapsibleSidebar.Toggle />
            </>
          )}
        >
          {renderSidebar(true)}
        </FilteredListPageLayout.Sidebar>
        <FilteredListPageLayout.Content as="div" width={width} padding={contentPadding} style={contentStyle}>
          {children}
        </FilteredListPageLayout.Content>
      </FilteredListPageLayout>
    )
  }

  return (
    <ThreePanesLayout
      contentAs="div"
      resizeable={false}
      leftPaneWidth="medium"
      collapsibleLeftPane={panes.collapsibleLeftPane}
      middlePane={<>{children}</>}
    />
  )
}
